import { Leaf, Salad, Wheat, Drumstick } from "lucide-react";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// App States
export const STATES = {
  HOME: "home",
  SCANNING: "scanning",
  INGREDIENTS: "ingredients",
  VIBE: "vibe",
  GENERATING: "generating",
  RECIPES: "recipes"
};

// Vibe data with images
export const VIBES = [
  { id: "chill", label: "Chill", emoji: "\u{1F33F}", image: "/images/vibes/chill.jpg", color: "#87CEEB" },
  { id: "bold", label: "Bold", emoji: "\u{1F525}", image: "/images/vibes/bold.jpg", color: "#E76F51" },
  { id: "calm", label: "Calm", emoji: "\u{1F30A}", image: "/images/vibes/calm.jpg", color: "#A8DADC" },
  { id: "cozy", label: "Cozy", emoji: "\u{2615}", image: "/images/vibes/cozy.jpg", color: "#DDA15E" },
  { id: "energized", label: "Energized", emoji: "\u{26A1}", image: "/images/vibes/energized.jpg", color: "#90BE6D" },
  { id: "hot", label: "Hot", emoji: "\u{1F336}\u{FE0F}", image: "/images/vibes/hot.jpg", color: "#F94144" }
];

// Diet modes
export const DIET_MODES = [
  { id: "vegetarian", label: "Vegetarian", icon: Leaf, color: "#2A9D8F", description: "No meat or fish" },
  { id: "vegan", label: "Vegan", icon: Salad, color: "#90BE6D", description: "No animal products" },
  { id: "glutenFree", label: "Gluten-Free", icon: Wheat, color: "#DDA15E", description: "No gluten" },
  { id: "keto", label: "Keto", icon: Drumstick, color: "#E76F51", description: "Low carb, high fat" }
];

// Cuisine options
export const CUISINES = [
  { id: null, label: "Any", emoji: "\u{1F30D}", color: "#264653" },
  { id: "italian", label: "Italian", emoji: "\u{1F1EE}\u{1F1F9}", color: "#E76F51" },
  { id: "mexican", label: "Mexican", emoji: "\u{1F1F2}\u{1F1FD}", color: "#F4A261" },
  { id: "asian", label: "Asian", emoji: "\u{1F962}", color: "#2A9D8F" },
  { id: "indian", label: "Indian", emoji: "\u{1F1EE}\u{1F1F3}", color: "#E9C46A" },
  { id: "mediterranean", label: "Mediterranean", emoji: "\u{1FAD2}", color: "#90BE6D" },
  { id: "american", label: "American", emoji: "\u{1F1FA}\u{1F1F8}", color: "#457B9D" }
];

// Dish type options
export const DISH_TYPES = [
  { id: "appetizer", label: "Appetizer", emoji: "\u{1F957}", color: "#2A9D8F" },
  { id: "salad", label: "Salad", emoji: "\u{1F96C}", color: "#90BE6D" },
  { id: "main_course", label: "Main Course", emoji: "\u{1F37D}\u{FE0F}", color: "#E76F51" },
  { id: "soup", label: "Soup", emoji: "\u{1F372}", color: "#F4A261" },
  { id: "dessert", label: "Dessert", emoji: "\u{1F370}", color: "#E9C46A" },
  { id: "drinks", label: "Drinks", emoji: "\u{1F964}", color: "#457B9D" }
];

// Category display config
export const CATEGORIES = [
  { key: "vegetables", label: "Vegetables", emoji: "\u{1F96C}" },
  { key: "fruits", label: "Fruits", emoji: "\u{1F34E}" },
  { key: "meats_seafood", label: "Meats & Seafood", emoji: "\u{1F969}" },
  { key: "dairy_eggs", label: "Dairy & Eggs", emoji: "\u{1F95B}" },
  { key: "grains_bread", label: "Grains & Bread", emoji: "\u{1F35E}" },
  { key: "pantry_other", label: "Pantry / Other", emoji: "\u{1FAD9}" }
];

// Quantity units for edit modal
export const QUANTITY_UNITS = [
  { value: "", label: "No unit" },
  { value: "pieces", label: "pieces", type: "solid" },
  { value: "cups", label: "cups", type: "both" },
  { value: "tbsp", label: "tbsp", type: "both" },
  { value: "tsp", label: "tsp", type: "both" },
  { value: "oz", label: "oz", type: "both" },
  { value: "lbs", label: "lbs", type: "solid" },
  { value: "g", label: "grams", type: "solid" },
  { value: "kg", label: "kg", type: "solid" },
  { value: "ml", label: "ml", type: "liquid" },
  { value: "L", label: "liters", type: "liquid" },
  { value: "bunch", label: "bunch", type: "solid" },
  { value: "carton", label: "carton", type: "both" },
  { value: "bottle", label: "bottle", type: "liquid" },
  { value: "can", label: "can", type: "both" },
  { value: "package", label: "package", type: "solid" },
  { value: "slice", label: "slices", type: "solid" },
];

// Playful loading messages for Stage 3
export const LOADING_MESSAGES = [
  { verb: "Chopping ideas\u2026", fact: "Honey never spoils and has been found perfectly preserved in ancient Egyptian tombs!" },
  { verb: "Saut\u00E9ing possibilities\u2026", fact: "Carrots were originally purple, and Dutch growers popularised orange ones in the 17th century." },
  { verb: "Balancing flavors\u2026", fact: "Pineapples can take up to two years to produce their first fruit, and each plant typically grows just one main pineapple." },
  { verb: "Negotiating with your fridge\u2026", fact: "Bananas are berries, but strawberries aren\u2019t\u2014botanically speaking!" },
  { verb: "Asking the eggs what they want to be\u2026", fact: "One of the oldest known drink \u201Crecipes\u201D describes Sumerian beer, and ancient Vedic texts likewise describe brewing the grain beer sur\u0101 over 3,000 years ago." },
  { verb: "Turning leftovers into legends\u2026", fact: "Cacao beans were so valuable in Mesoamerica that the Aztecs and Maya used them as a form of currency." },
  { verb: "Whisking up magic\u2026", fact: "Apples float in water because roughly a quarter of their volume is air." },
  { verb: "Measuring twice, cooking once\u2026", fact: "Nutmeg was once so valuable it helped spark brutal colonial battles over its trade in the Banda Islands." },
  { verb: "Consulting the recipe spirits\u2026", fact: "Peanuts aren\u2019t nuts at all\u2014they\u2019re legumes that grow underground!" },
  { verb: "Tasting the future\u2026", fact: "Tomatoes in Europe were long grown as ornamental plants because many people thought they were poisonous." },
  { verb: "Stirring creativity\u2026", fact: "The fortune cookie was invented in California in the early 1900s, not in China." },
  { verb: "Preheating imagination\u2026", fact: "Cashews grow in shells that hang from the bottom of a cashew apple!" },
  { verb: "Folding in possibilities\u2026", fact: "In the 1830s, tomato ketchup was sold as a medicine to treat indigestion." },
  { verb: "Dicing decisions\u2026", fact: "White chocolate isn\u2019t really chocolate\u2014it\u2019s made from cocoa butter and contains no cocoa solids." },
  { verb: "Simmering inspiration\u2026", fact: "Astronauts often can\u2019t taste food as well in space because fluid shifts in microgravity make them feel a bit \u201Ccongested.\u201D" },
  { verb: "Zesting enthusiasm\u2026", fact: "Lemons taste more sour than strawberries because their high acidity masks their natural sugars." },
  { verb: "Marinating memories\u2026", fact: "The world\u2019s most expensive spice, saffron, comes from a crocus flower and must be carefully hand\u2011harvested." },
  { verb: "Sprinkling wonder\u2026", fact: "Cucumbers are about 96% water, making them one of the most hydrating foods you can eat." },
  { verb: "Garnishing dreams\u2026", fact: "Almonds are seeds, not true nuts, and are close relatives of peaches in the same plant family." },
  { verb: "Plating perfection\u2026", fact: "Vanilla comes from orchids and is the second most expensive spice after saffron." }
];

// Scanning stage messages
export const SCANNING_MESSAGES = [
  "Cleaning my glasses\u2026 \u{1F453}",
  "Squinting at your fridge\u2026",
  "Ooooh what\u2019s that?",
  "Is that\u2026 garlic?",
  "Hold on, zooming in\u2026 \u{1F50D}",
  "Playing ingredient detective\u2026",
  "I know I\u2019ve seen that before\u2026",
  "Double-checking with my eyeballs\u2026",
  "Asking the veggies to introduce themselves\u2026",
  "Giving your ingredients a once-over\u2026",
  "Staring respectfully at your food\u2026",
  "Taking a closer look\u2026",
  "Making eye contact with your produce\u2026",
  "Counting carrots\u2026 \u{1F955}",
  "Negotiating with onions\u2026 \u{1F9C5}",
  "Having a chat with the eggs\u2026 \u{1F95A}",
  "Side-eyeing the cheese\u2026 \u{1F9C0}",
  "Respecting the avocado\u2026 \u{1F951}"
];
